import React, { useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import Button, { IconButton } from '@atlaskit/button/new';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import CrossIcon from '@atlaskit/icon/core/cross';
import InformationIcon from '@atlaskit/icon/core/status-information';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	UNSTABLE_configItems,
	UNSTABLE_DO_NOT_USE_ONLY_FOR_CONFLUENCE_triggerConfigItem,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentId } from '@confluence/page-restrictions-context/entry-points/useContentId.ts';

import polishIllustration from '../assets/polish-hover-card-illustration.png';
import { useCfind5926UploadInOnboarding } from '../hooks/useCfind5926UploadInOnboarding.ts';
import { useIsImportedPage } from '../hooks/useIsImportedPage.ts';
import { usePolishBannerDismissal } from '../hooks/usePolishBannerDismissal.ts';

const hoverCardStyles = cssMap({
	wrapper: {
		width: '320px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		overflow: 'hidden',
	},
	content: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	footer: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	footerText: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	title: {
		font: token('font.heading.small'),
		color: token('color.text'),
	},
	description: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
	},
});

const bannerStyles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		width: '100%',
		boxSizing: 'border-box',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.250'),
		paddingInlineEnd: token('space.200'),
		gap: token('space.100'),
	},
	content: {
		flex: '1',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0px',
	},
	text: {
		font: token('font.body.small'),
		color: token('color.text'),
		flexShrink: 1,
		minWidth: '0px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const hoverCardFooterBorderStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
	borderTop: `1px solid ${token('color.border.focused')}`,
});

const illustrationStyles = css({
	display: 'block',
	width: '100%',
	height: '128px',
	objectFit: 'cover',
	borderTopLeftRadius: token('radius.small'),
	borderTopRightRadius: token('radius.small'),
});

const i18n = defineMessages({
	bannerText: {
		id: 'experiment-cfind-5926-upload-in-onboarding.banner.text',
		defaultMessage: 'Automatically format content without changing what you wrote.',
		description: 'Text shown in the banner prompting the user to use the AI polish feature.',
	},
	addPolishButton: {
		id: 'experiment-cfind-5926-upload-in-onboarding.banner.add-polish-button',
		defaultMessage: 'Improve formatting',
		description: 'Button label to trigger the AI polish feature on an imported page.',
	},
	dismissButton: {
		id: 'experiment-cfind-5926-upload-in-onboarding.banner.dismiss-button',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button that dismisses the imported page banner.',
	},
	hoverCardTitle: {
		id: 'experiment-cfind-5926-upload-in-onboarding.hover-card.title',
		defaultMessage: 'Get a polished doc in one click',
		description: 'Title of the hover card explaining the AI polish feature.',
	},
	hoverCardDescription: {
		id: 'experiment-cfind-5926-upload-in-onboarding.hover-card.description',
		defaultMessage:
			'Use Rovo to restructure headings, lists, and more. It won’t undo any of your work.',
		description: 'Description text in the hover card for the AI polish feature.',
	},
	hoverCardFooter: {
		id: 'experiment-cfind-5926-upload-in-onboarding.hover-card.footer',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Footer text in the hover card indicating this feature is AI-powered.',
	},
});

const PolishHoverCard = () => {
	const intl = useIntl();
	return (
		<Box xcss={hoverCardStyles.wrapper}>
			<img css={illustrationStyles} src={polishIllustration} alt="" role="presentation" />
			<Stack xcss={hoverCardStyles.content} space="space.100">
				<Stack space="space.100">
					<Box as="span" xcss={hoverCardStyles.title}>
						{intl.formatMessage(i18n.hoverCardTitle)}
					</Box>
					<Box as="span" xcss={hoverCardStyles.description}>
						{intl.formatMessage(i18n.hoverCardDescription)}
					</Box>
				</Stack>
			</Stack>
			<div css={hoverCardFooterBorderStyles}>
				<Inline xcss={hoverCardStyles.footer} space="space.050" alignBlock="center">
					<InformationIcon label="" color={token('color.icon.subtlest')} />
					<Box as="span" xcss={hoverCardStyles.footerText}>
						{intl.formatMessage(i18n.hoverCardFooter)}
					</Box>
				</Inline>
			</div>
		</Box>
	);
};

type BannerContentProps = {
	editorActions: { _privateGetEditorView: () => EditorView | undefined };
	dismiss: (fireAnalytic?: boolean) => void;
};

const BannerContentComponent = ({ editorActions, dismiss }: BannerContentProps) => {
	const intl = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleAddPolish = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				source: 'cfind-5926-upload-in-onboarding-use-polish-button',
			},
		}).fire();

		const editorView = editorActions._privateGetEditorView();
		if (!editorView) return;
		UNSTABLE_DO_NOT_USE_ONLY_FOR_CONFLUENCE_triggerConfigItem({
			configItem: UNSTABLE_configItems.addPolishConfigItem(),
			editorView,
		});
		// Clicking "Add Polish" is also a form of dismissal, passing false for it to not fire button clicked analytics on dismiss
		dismiss(false);
	}, [editorActions, dismiss, createAnalyticsEvent]);

	const handleButtonMouseEnter = useCallback(() => {
		hoverTimerRef.current = setTimeout(() => setIsHoverCardOpen(true), 300);
	}, []);

	const handleButtonMouseLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
		setIsHoverCardOpen(false);
	}, []);

	return (
		<Box xcss={bannerStyles.wrapper} backgroundColor="color.background.accent.purple.subtlest">
			<Box xcss={bannerStyles.content}>
				<Box as="span" xcss={bannerStyles.text}>
					{intl.formatMessage(i18n.bannerText)}
				</Box>
				<Popup
					isOpen={isHoverCardOpen}
					onClose={() => setIsHoverCardOpen(false)}
					placement="bottom-start"
					content={() => <PolishHoverCard />}
					trigger={(triggerProps) => (
						<div
							{...triggerProps}
							onMouseEnter={handleButtonMouseEnter}
							onFocus={handleButtonMouseEnter}
							onMouseLeave={handleButtonMouseLeave}
							onBlur={handleButtonMouseLeave}
						>
							<Button
								appearance="subtle"
								onClick={handleAddPolish}
								iconBefore={() => (
									<AiGenerativeCleanupIcon label="" color={token('color.icon.subtle')} />
								)}
							>
								{intl.formatMessage(i18n.addPolishButton)}
							</Button>
						</div>
					)}
				/>
			</Box>
			<IconButton
				appearance="subtle"
				icon={() => <CrossIcon label={intl.formatMessage(i18n.dismissButton)} />}
				label={intl.formatMessage(i18n.dismissButton)}
				onClick={() => dismiss()}
			/>
		</Box>
	);
};

type ImportedPagePolishBannerProps = {
	editorActions: { _privateGetEditorView: () => EditorView | undefined };
};

export const ImportedPagePolishBanner = ({ editorActions }: ImportedPagePolishBannerProps) => {
	const rawContentId = useContentId();
	const contentId: string | undefined = typeof rawContentId === 'string' ? rawContentId : undefined;
	const isLivePage = useIsCurrentPageLive();
	const { isEnabled, loading } = useCfind5926UploadInOnboarding();
	const { isImported, isLivePage: isLivePageFromQuery } = useIsImportedPage(contentId ?? '');
	const { dismissed, dismiss } = usePolishBannerDismissal(contentId ?? '');
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId: contentId ?? '',
	});

	// Use isLivePage from the query result rather than LivePagesStore to avoid a timing
	// issue where the store hasn't been updated yet on first SPA navigation to the imported page.
	const isPageLive = isLivePage || isLivePageFromQuery;
	if (
		!contentId ||
		!isPageLive ||
		loading ||
		!isEnabled ||
		!isImported ||
		!isAIEnabledForContent ||
		!isRovoEnabledForContent ||
		dismissed
	) {
		return null;
	}

	return <BannerContentComponent editorActions={editorActions} dismiss={dismiss} />;
};
